import { supabase } from './supabaseClient';
import notificationSound from '../assets/sounds/notification.mp3';
// import { emailService } from '../services/emailService'; // emailService import edildi

/**
 * Bildirim gönderme servisi
 * Bu servis, uygulamanın farklı bölümlerinde bildirim oluşturmak için kullanılır
 */

// Bildirim sesi için audio nesnesi
const notificationAudio = new Audio(notificationSound);

// Bildirim ses ayarlarını localStorage'dan al
const getNotificationSoundSettings = () => {
  try {
    const settings = localStorage.getItem('notificationSettings');
    if (settings) {
      return JSON.parse(settings);
    }
    return { sound: true }; // Varsayılan olarak ses açık
  } catch {
    return { sound: true };
  }
};

// Bildirim ses ayarlarını güncelle
export const updateNotificationSoundSettings = (enabled) => {
  try {
    localStorage.setItem('notificationSettings', JSON.stringify({ sound: enabled }));
  } catch (error) {
    console.error('Bildirim ayarları kaydedilemedi:', error);
  }
};

// Bildirim sesi çalma fonksiyonunu güncelleyelim
export const playNotificationSound = () => {
  try {
    const settings = getNotificationSoundSettings();
    if (settings.sound) {
      notificationAudio.currentTime = 0;
      notificationAudio.play();
    }
  } catch (error) {
    console.error('Bildirim sesi çalınamadı:', error);
  }
};

/**
 * Bildirimi veritabanına kaydet
 * @param {string} userId - Bildirim alıcısının ID'si
 * @param {string} createdBy - Bildirimi oluşturan kullanıcının ID'si (null olabilir)
 * @param {string} title - Bildirim başlığı
 * @param {string} content - Bildirim içeriği
 * @param {string} type - Bildirim tipi: 'task', 'project', 'team', 'mention', 'system', 'security'
 * @param {string} relatedId - İlgili öğenin ID'si (task, project, vb.)
 * @param {string} link - Bildirime tıklandığında yönlendirilecek link
 * @param {object} metadata - Ek bilgiler
 * @returns {Promise<object>} Oluşturulan bildirim
 */
export const createNotification = async (
  userId,
  createdBy,
  title,
  content,
  type,
  relatedId = null,
  link = null,
  metadata = {}
) => {
  try {
    // Çift bildirim kontrolü
    const isDuplicate = await checkDuplicateNotification(userId, type, relatedId);
    if (isDuplicate) {
      // console.log('Benzer bildirim zaten var, tekrar gönderilmedi');
      return null;
    }

    // Bildirim alıcısının e-posta adresini al
    const { data: userProfile, error: userProfileError } = await supabase
      .from('user_profiles')
      .select('email')
      .eq('id', userId)
      .single();

    if (userProfileError) {
      console.error('Kullanıcı e-posta adresi alınamadı:', userProfileError);
      // Hata olsa bile bildirim oluşturma işlemine devam et
    }

    const { data, error } = await supabase.rpc('notify_user', {
      p_user_id: userId,
      p_created_by: createdBy,
      p_title: title,
      p_content: content,
      p_type: type,
      p_related_id: relatedId,
      p_link: link,
      p_metadata: metadata
    });

    if (error) throw error;

    // Bildirim başarıyla oluşturulduysa ve e-posta adresi varsa e-posta gönder
    if (data && userProfile?.email) {
      const emailSubject = `Yeni Bildirim: ${title}`;
      // const emailHtml = `...`; // HTML oluşturma sunucuya taşındı
      
      try {
        await fetch(`${import.meta.env.VITE_SERVER_BASE_URL}/api/send-email`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            to: userProfile.email,
            subject: emailSubject,
            content: content, // İçeriği de gönder
            notificationType: type, // Bildirim tipini gönder
            relatedId: relatedId, // İlgili ID'yi gönder
            metadata: metadata, // Metadata'yı gönder
            link: link, // Linki clientLink olarak gönder
          }),
        });
        // console.log(`E-posta gönderme isteği başarıyla gönderildi: ${userProfile.email}`);
      } catch (emailError) {
        console.error('E-posta gönderme isteği hatası:', emailError);
      }
    }

    return data;
  } catch (error) {
    console.error('Bildirim oluşturma hatası:', error);
    throw error;
  }
};

const checkDuplicateNotification = async (userId, type, relatedId, timeWindow = 300) => {
  try {
    // Ana sorgu oluştur
    let query = supabase
      .from('notifications')
      .select('id')
      .eq('user_id', userId)
      .eq('type', type)
      .gte('created_at', new Date(Date.now() - timeWindow * 1000).toISOString())
      .limit(1);
    
    // relatedId undefined veya null değilse ekle, değilse bu koşulu atlama
    if (relatedId !== undefined && relatedId !== null) {
      query = query.eq('related_id', relatedId);
    } else {
      // relatedId null ise, veritabanında da null olan kayıtları kontrol et
      query = query.is('related_id', null);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data.length > 0;
  } catch (error) {
    console.error('Bildirim kontrolü hatası:', error);
    return false;
  }
};

/**
 * Bir göreve atanan tüm kullanıcılara bildirim gönder
 * @param {object} task - Görev nesnesi
 * @param {string} creatorId - Görevi oluşturan kullanıcının ID'si
 * @param {string} creatorName - Görevi oluşturan kullanıcının adı
 */
export const notifyTaskAssignees = async (task, creatorId, creatorName) => {
  try {
    if (!task.assigned_members || task.assigned_members.length === 0) return;

    // JSON verisini parse et
    const assignees = typeof task.assigned_members === 'string' 
      ? JSON.parse(task.assigned_members) 
      : task.assigned_members;
    
    // Her atanan üye için bildirim oluştur (kullanıcının kendisi hariç)
    for (const assignee of assignees) {
      const assigneeId = assignee.id || assignee;
      
      // Kullanıcı kendisine görev atadıysa bildirim gönderme
      if (assigneeId === creatorId) continue;
      
      await createNotification(
        assigneeId,
        creatorId,
        'Yeni Görev Atandı',
        `${creatorName} size "${task.title}" görevini atadı.`,
        'task',
        task.id,
        `/tasks/${task.id}`,
        {
          task_title: task.title,
          priority: task.priority,
          project_id: task.project_id,
          due_date: task.end_date
        }
      );
    }
  } catch (error) {
    console.error('Görev bildirim hatası:', error.message);
  }
};

/**
 * Proje oluşturulduğunda yöneticilere ve ekip liderlerine bildirim gönder
 * @param {object} project - Proje nesnesi 
 * @param {string} creatorId - Projeyi oluşturan kullanıcının ID'si
 * @param {string} creatorName - Projeyi oluşturan kullanıcının adı
 * @param {string} projectType - Proje türü: 'projects' veya 'consulting'
 */
export const notifyProjectCreation = async (project, creatorId, creatorName, projectType = 'projects') => {
  try {
    // Sadece Super Admin ve Admin rolündeki kullanıcıları bul
    const { data: managers, error } = await supabase
      .from('user_profiles')
      .select('id, role, first_name, last_name')
      .in('role', ['super', 'admin']);
    
    if (error) throw error;
    
    // Link ve title belirle
    const link = projectType === 'consulting' ? `/consulting` : `/projects/${project.id}`;
    const title = projectType === 'consulting' ? 'Yeni Firma Oluşturuldu' : 'Yeni Proje Oluşturuldu';
    const content = projectType === 'consulting' 
      ? `${creatorName} "${project.name}" adında yeni bir firma oluşturdu.`
      : `${creatorName} "${project.name}" adında yeni bir proje oluşturdu.`;
    
    // Her Super Admin ve Admin'e bildirim gönder (oluşturan kişi hariç)
    for (const manager of managers) {
      if (manager.id === creatorId) continue;
      
      await createNotification(
        manager.id,
        creatorId,
        title,
        content,
        'project',
        project.id,
        link,
        {
          project_name: project.name,
          status: project.status,
          start_date: project.start_date,
          end_date: project.end_date,
          project_type: projectType
        }
      );
    }
  } catch (error) {
    console.error('Proje bildirim hatası:', error.message);
  }
};

/**
 * Alt proje oluşturulduğunda ana proje sahibine bildirim gönder
 * @param {object} subProject - Alt proje nesnesi
 * @param {string} parentProjectId - Ana proje ID'si
 * @param {string} parentProjectName - Ana proje adı
 * @param {string} creatorId - Alt projeyi oluşturan kullanıcının ID'si
 * @param {string} projectType - Proje türü: 'projects' veya 'consulting'
 */
export const notifySubProjectCreation = async (
  subProject, 
  parentProjectId, 
  parentProjectName, 
  creatorId,
  projectType = 'projects' // Varsayılan olarak 'projects' tablosunu kullan
) => {
  try {
    // ParentProjectId'nin formatını kontrol et ve debug bilgisi yazdır
    console.log(`Alt proje bildirimi gönderiliyor: ${subProject.name} -> Proje ID: ${parentProjectId} (${typeof parentProjectId}), Tür: ${projectType}`);
    
    // Eğer ID sayısal bir değerse (consulting.jsx'in gönderdiği gibi), string'e çevir
    if (typeof parentProjectId === 'number') {
      parentProjectId = parentProjectId.toString();
    }

    // ParentProjectId'nin geçerli bir UUID olup olmadığını kontrol et
    const isUuid = typeof parentProjectId === 'string' && 
                 (parentProjectId.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i) ||
                  parentProjectId.match(/^[0-9a-f]{32}$/i));

    // Consulting projeleri için numeric ID formatını da kabul et
    const isNumericId = /^\d+$/.test(parentProjectId);
    
    // Tabloyu seç
    const tableName = projectType === 'consulting' ? 'consulting' : 'projects';
    
    // ID formatı doğru değilse uyarı ver ve çık
    if (!isUuid && !isNumericId) {
      console.log(`Geçersiz ProjectId formatı: ${parentProjectId}. İşlem atlanıyor.`);
      return;
    }

    // Proje sahibini bul
    const { data: projectData, error: projectError } = await supabase
      .from(tableName)
      .select('created_by')
      .eq('id', parentProjectId)
      .single();

    if (projectError) {
      console.error(`${tableName} bilgileri alınamadı:`, projectError);
      return;
    }

    // Bildirim alıcılarını belirle (proje sahibi)
    const recipientId = projectData.created_by;

    // Eğer bildirim gönderen kişi, projenin sahibi değilse bildirim oluştur
    if (recipientId !== creatorId) {
      // Bildirim gönderen kişinin bilgilerini al
      const { data: creatorProfile, error: creatorError } = await supabase
        .from('user_profiles')
        .select('first_name, last_name')
        .eq('id', creatorId)
        .single();

      if (creatorError) {
        console.error('Kullanıcı profili alınamadı:', creatorError);
        return;
      }

      // Bildirim oluştur
      const { error: notificationError } = await supabase
        .from('notifications')
        .insert([
          {
            user_id: recipientId,
            title: 'Yeni Alt Proje Eklendi',
            content: `${creatorProfile.first_name} ${creatorProfile.last_name}, "${parentProjectName}" projesine "${subProject.name}" alt projesini ekledi.`,
            type: 'project',
            created_by: creatorId,
            project_id: projectType === 'consulting' ? null : parentProjectId, // Consulting için null gönder
            sub_project_id: subProject.id
          }
        ]);

      if (notificationError) {
        console.error('Bildirim oluşturulamadı:', notificationError);
      } else {
        console.log('Bildirim başarıyla oluşturuldu');
      }
    }
  } catch (error) {
    console.error('Alt proje bildirim hatası:', error);
  }
};

/**
 * Takım üyesi eklendiğinde veya güncellendiğinde yöneticilere bildirim gönder
 * @param {object} member - Takım üyesi nesnesi
 * @param {string} memberName - Takım üyesinin adı
 * @param {string} action - Eylem tipi: 'added', 'updated'
 */
export const notifyTeamMemberChange = async (member, memberName, action) => {
  try {
    // Sadece Super Admin ve Admin rolündeki kullanıcıları bul
    const { data: managers, error } = await supabase
      .from('user_profiles')
      .select('id, role, first_name, last_name')
      .in('role', ['super', 'admin']);
    
    if (error) throw error;
    
    const title = action === 'added' ? 'Yeni Ekip Üyesi Eklendi' : 'Ekip Üyesi Güncellendi';
    const content = action === 'added'
      ? `${memberName} ekibe katıldı. Departman: ${member.department || 'Belirtilmemiş'}`
      : `${memberName} profilini güncelledi.`;
    
    // Her Super Admin ve Admin'e bildirim gönder (kendisi hariç)
    for (const manager of managers) {
      if (manager.id === member.user_id) continue;
      
      await createNotification(
        manager.id,
        member.user_id,
        title,
        content,
        'team',
        member.user_id,
        '/team',
        {
          member_name: memberName,
          department: member.department,
          role: member.role,
          action: action
        }
      );
    }
  } catch (error) {
    console.error('Takım üyesi bildirim hatası:', error.message);
  }
};

/**
 * Görev durumu değiştiğinde atanan kişilere bildirim gönder
 * @param {object} task - Güncellenmiş görev nesnesi
 * @param {string} oldStatus - Eski durum
 * @param {string} updaterName - Güncelleyen kişinin adı
 * @param {string} updaterId - Güncelleyen kişinin ID'si
 */
export const notifyTaskStatusChange = async (task, oldStatus, updaterName, updaterId) => {
  try {
    if (!task.assigned_members || task.assigned_members.length === 0) return;
    
    // JSON verisini parse et
    const assignees = typeof task.assigned_members === 'string' 
      ? JSON.parse(task.assigned_members) 
      : task.assigned_members;
    
    // Her atanan üye için bildirim oluştur (güncelleyen kişi hariç)
    for (const assignee of assignees) {
      const assigneeId = assignee.id || assignee;
      
      // Güncelleyen kişi kendisiyse bildirim gönderme
      if (assigneeId === updaterId) continue;
      
      await createNotification(
        assigneeId,
        updaterId,
        'Görev Durumu Değiştirildi',
        `${updaterName} "${task.title}" görevinin durumunu "${oldStatus}" durumundan "${task.status}" durumuna güncelledi.`,
        'task',
        task.id,
        `/tasks/${task.id}`,
        {
          task_title: task.title,
          old_status: oldStatus,
          new_status: task.status
        }
      );
    }
  } catch (error) {
    console.error('Görev durum değişikliği bildirim hatası:', error.message);
  }
};

/**
 * Kullanıcı takip edildiğinde bildirim gönder
 * @param {string} followerId - Takip eden kullanıcının ID'si
 * @param {string} followerName - Takip eden kullanıcının adı
 * @param {string} followingId - Takip edilen kullanıcının ID'si
 */
export const notifyUserFollowed = async (followerId, followerName, followingId) => {
  try {
    // Özel takip bildirimi fonksiyonunu çağır
    const { data, error } = await supabase
      .rpc('notify_user_followed', {
        p_follower_id: followerId,
        p_follower_name: followerName,
        p_following_id: followingId
      });

    if (error) throw error;
    
    // console.log(`Takip bildirimi gönderildi: ${followerName} -> ${followingId}`);
    return data;
  } catch (error) {
    console.error('Takip bildirim hatası:', error.message);
  }
};

/**
 * Ekip üyesi profil güncellemesi bildirimini gönder
 * @param {string} userId - Güncellemeyi yapan kullanıcının ID'si
 * @param {string} userName - Güncellemeyi yapan kullanıcının adı
 * @param {string} updateType - Güncelleme tipi: 'bio' veya 'skills'
 * @param {string} [details] - Opsiyonel ek bilgiler (super kullanıcı editleri için)
 * @returns {Promise<void>}
 */
export const notifyProfileUpdate = async (userId, userName, updateType, details) => {
  try {
    // Sadece yöneticilere ve ekip liderlerine bildirim gönder
    const { data: managers, error } = await supabase
      .from('user_profiles')
      .select('id')
      .or('job_position.ilike.%Yönetici%,job_position.ilike.%Lider%');
    
    if (error) throw error;
    
    const updateTypeText = updateType === 'bio' ? 'biyografisini' : 'yeteneklerini';
    let notificationContent = `${userName} ${updateTypeText} güncelledi`;
    
    // Super kullanıcı editleri için ekstra bilgi ekle
    if (details) {
      notificationContent += ` (${details})`;
    }
    
    for (const manager of managers) {
      if (manager.id !== userId) { // Kendisine bildirim gönderme
        await createNotification(
          manager.id,
          userId,
          'Ekip Üyesi Profil Güncellemesi',
          notificationContent,
          'team',
          userId,
          '/team'
        );
      }
    }
  } catch (error) {
    console.error('Profil güncelleme bildirimi hatası:', error);
  }
};

/**
 * Görev tamamlandığında ilgili üyelere bildirim gönder
 * @param {object} task - Tamamlanan görev
 * @param {string} completerId - Görevi tamamlayan kullanıcının ID'si
 * @param {string} completerName - Görevi tamamlayan kullanıcının adı
 */
export const notifyTaskCompleted = async (task, completerId, completerName) => {
  try {
    if (!task.assigned_members || task.assigned_members.length === 0) return;

    const assignees = typeof task.assigned_members === 'string'
      ? JSON.parse(task.assigned_members)
      : task.assigned_members;

    const notifyUsers = new Set([task.created_by, ...assignees.map(a => a.id)]);
    
    for (const userId of notifyUsers) {
      if (userId === completerId) continue;

      await createNotification(
        userId,
        completerId,
        'Görev Tamamlandı',
        `${completerName} "${task.title}" görevini tamamladı.`,
        'task',
        task.id,
        `/tasks?taskId=${task.id}`,
        {
          task_title: task.title,
          project_id: task.project_id,
          completion_date: new Date().toISOString()
        }
      );
    }
  } catch (error) {
    console.error('Görev tamamlama bildirimi hatası:', error.message);
  }
};

/**
 * Görev silindiğinde ilgili üyelere bildirim gönder
 * @param {object} task - Silinen görev
 * @param {string} deleterId - Görevi silen kullanıcının ID'si
 * @param {string} deleterName - Görevi silen kullanıcının adı
 */
export const notifyTaskDeleted = async (task, deleterId, deleterName) => {
  try {
    if (!task.assigned_members || task.assigned_members.length === 0) return;

    const assignees = typeof task.assigned_members === 'string'
      ? JSON.parse(task.assigned_members)
      : task.assigned_members;

    const notifyUsers = new Set([task.created_by, ...assignees.map(a => a.id)]);
    
    for (const userId of notifyUsers) {
      if (userId === deleterId) continue;

      await createNotification(
        userId,
        deleterId,
        'Görev Silindi',
        `${deleterName} "${task.title}" görevini sildi.`,
        'task',
        undefined,
        '/tasks',
        {
          task_title: task.title,
          project_id: task.project_id,
          deletion_date: new Date().toISOString()
        }
      );
    }
  } catch (error) {
    console.error('Görev silme bildirimi hatası:', error.message);
  }
};

/**
 * Görev güncellendiğinde ilgili üyelere bildirim gönder
 * @param {object} task - Güncellenen görev
 * @param {string} updaterId - Görevi güncelleyen kullanıcının ID'si
 * @param {string} updaterName - Görevi güncelleyen kullanıcının adı
 * @param {object} originalTask - Güncelleme öncesi orijinal görev
 */
export const notifyTaskUpdated = async (task, updaterId, updaterName, originalTask) => {
  try {
    // Atanan üyeleri belirle
    let newAssignees = [];
    if (task.assigned_members) {
      newAssignees = typeof task.assigned_members === 'string'
        ? JSON.parse(task.assigned_members)
        : task.assigned_members;
    }

    let originalAssignees = [];
    if (originalTask && originalTask.assigned_members) {
      originalAssignees = typeof originalTask.assigned_members === 'string'
        ? JSON.parse(originalTask.assigned_members)
        : originalTask.assigned_members;
    }

    const newMemberIds = new Set(newAssignees.map(a => a.id || a));
    const originalMemberIds = new Set(originalAssignees.map(a => a.id || a));

    // Değişen alanları belirle (atanan üyeler hariç)
    const changedFields = [];
    if (originalTask) {
      if (task.title !== originalTask.title) changedFields.push('Görev Başlığı');
      if (task.description !== originalTask.description) changedFields.push('Açıklama Metni');
      if (task.priority !== originalTask.priority) changedFields.push('Öncelik Durumu');
      if (task.start_date !== originalTask.start_date || task.end_date !== originalTask.end_date) changedFields.push('Proje Tarihleri');
      if (task.notes !== originalTask.notes) changedFields.push('Notlar');
      
      // Ekler değiştiyse
      const newAttachments = JSON.stringify(task.attachments || []);
      const originalAttachments = JSON.stringify(originalTask.attachments || []);
      if (newAttachments !== originalAttachments) {
        changedFields.push('Ekler');
      }

      // Proje değişikliklerini kontrol et
      if (task.project_id !== originalTask.project_id) {
        changedFields.push('Ana Proje');
      }
      if (task.sub_project_id !== originalTask.sub_project_id) {
        changedFields.push('Alt Proje');
      }
      if (task.consulting_project_id !== originalTask.consulting_project_id) {
        changedFields.push('Danışmanlık Projesi');
      }
      if (task.consulting_parent_id !== originalTask.consulting_parent_id) {
        changedFields.push('Danışmanlık Firması');
      }

      // Kategoriler değiştiyse
      const newCategoryIds = new Set((task.categories || []).map(c => c.id || c));
      const originalCategoryIds = new Set((originalTask.categories || []).map(c => c.id || c));
      
      if (newCategoryIds.size !== originalCategoryIds.size ||
          [...newCategoryIds].some(id => !originalCategoryIds.has(id)) ||
          [...originalCategoryIds].some(id => !newCategoryIds.has(id))) {
        changedFields.push('Kategoriler');
      }

    }

    // Her kullanıcıya bildirim gönder
    for (const userId of allRelevantUserIds) {
      // Güncellemeyi yapan kişiye bildirim gönderme
      if (userId === updaterId) continue;

      // Kullanıcının atanan üyeler listesinde olup olmadığını kontrol et
      const isCurrentlyAssigned = newMemberIds.has(userId);
      const wasPreviouslyAssigned = originalMemberIds.has(userId);

      let title = 'Görev Güncellendi';
      let content = '';

      if (addedMembers.some(m => m.id === userId)) {
        // Bu kullanıcı yeni atanmış bir üye
        title = 'Göreve Atandınız';
        content = `${updaterName} sizi "${task.title}" görevine atadı.`;
      } else if (removedMembers.some(m => m.id === userId)) {
        // Bu kullanıcının ataması kaldırılmış
        title = 'Görevden Çıkarıldınız';
        content = `${updaterName} sizi "${task.title}" görevinden çıkardı.`;
      } else if (userId === task.created_by && task.created_by !== updaterId) {
        // Görevi oluşturan kişi güncelleyici değilse ve görev güncellendiyse
        if (changedFields.length > 0) {
          content = `${updaterName} "${task.title}" görevinde değişiklikler yaptı. Değişen alanlar: ${changedFields.join(', ')}.`;
        } else if (addedMembers.length > 0 || removedMembers.length > 0) {
          // Sadece atanan üyelerde değişiklik olduysa ancak oluşturan kişi de bildirim almalı
          content = `${updaterName} "${task.title}" görevinin atanan üyelerinde değişiklik yaptı.`;
        } else {
          // Diğer durumlar (örneğin atanmamış veya ilgili olmayan) için bildirim gönderme
          continue;
        }
      } else if (isCurrentlyAssigned && wasPreviouslyAssigned && (changedFields.length > 0 || addedMembers.length > 0 || removedMembers.length > 0)) {
        // Önceden atanmış olan ve hala atanmış olan üyelere (güncelleyen kişi değilse) genel güncelleme bildirimi
        // Hem diğer alanlarda değişiklik hem de atananlarda değişiklik varsa bu bildirimi gönder
        if (changedFields.length > 0 && (addedMembers.length > 0 || removedMembers.length > 0)) {
          content = `${updaterName} "${task.title}" görevinde değişiklikler yaptı (alanlar: ${changedFields.join(', ')}) ve atanan üyeleri güncelledi.`;
        } else if (changedFields.length > 0) {
          content = `${updaterName} "${task.title}" görevinde değişiklikler yaptı. Değişen alanlar: ${changedFields.join(', ')}.`;
        } else if (addedMembers.length > 0 || removedMembers.length > 0) {
          content = `${updaterName} "${task.title}" görevinin atanan üyelerinde değişiklik yaptı.`;
        } else {
          continue;
        }
      } else {
        // Diğer durumlar için bildirim gönderme
        continue;
      }

      await createNotification(
        userId,
        updaterId,
        title,
        content,
        'task',
        task.id,
        `/tasks?taskId=${task.id}`,
        {
          task_title: task.title,
          project_id: task.project_id,
          update_date: new Date().toISOString(),
          changed_fields: changedFields,
          old_assigned_members: originalAssignees.map(a => a.id || a),
          new_assigned_members: newAssignees.map(a => a.id || a),
        }
      );
    }
  } catch (error) {
    console.error('Görev güncelleme bildirimi hatası:', error.message);
  }
};

/**
 * Yeni paylaşılan dosya bildirimi gönder
 * @param {string} userId - Bildirim alıcısının ID'si
 * @param {string} createdBy - Bildirimi oluşturan kullanıcının ID'si (null olabilir)
 * @param {string} sharerName - Paylaşan kullanıcının adı
 * @param {string} fileName - Paylaşılan dosyanın adı
 * @param {string} shareId - Paylaşılan dosyanın ID'si
 */
export const notifyFileShare = async (userId, createdBy, sharerName, fileName, shareId) => {
  try {
    await createNotification(
      userId,
      createdBy,
      'Yeni Paylaşılan Dosya',
      `${sharerName}, "${fileName}" dosyasını sizinle paylaştı.`,
      'system',
      shareId,
      '/files',
      {
        file_name: fileName,
        share_type: 'specific',
        shared_at: new Date().toISOString(),
        notification_subtype: 'file_share',
        file_id: shareId
      }
    );
  } catch (error) {
    console.error('Dosya paylaşımı bildirimi hatası:', error.message);
  }
}; 